import { useEffect, useRef } from 'react';
import { PixelRatio } from 'react-native';
import { MESSAGE_INPUT_HEIGHT } from '../constants/ui';

/**
 * Fixed Height Text Input Hook
 *
 * **BREAKING CHANGE**: This hook was refactored from auto-grow to fixed-height (Phase 4).
 * - Returns no-op handlers for focus/blur/contentSizeChange (not used in fixed-height mode)
 * - Always returns `isNearMaxHeight: true` since height is constant
 * - Callers expecting dynamic height behavior should migrate to this fixed-height approach
 *
 * Fixed height approach (no auto-grow):
 * - All users: 168px (6 lines) - prevents keyboard cutting
 * - Height stays constant in default and focus mode (keyboard active)
 * - Content scrolls internally when needed
 * - Simplified logic for keyboard-based FAB docking
 */
export function useAutoGrowInput(
  onHeightChange?: (height: number) => void
) {
  // Use centralized constant to prevent documentation drift
  // See: apps/mobile/constants/ui.ts for calculation details
  const FIXED_HEIGHT = PixelRatio.roundToNearestPixel(MESSAGE_INPUT_HEIGHT);

  // Use ref to stabilize callback and prevent unnecessary re-renders
  const callbackRef = useRef(onHeightChange);
  useEffect(() => {
    callbackRef.current = onHeightChange;
  }, [onHeightChange]);

  // Notify parent of height once on mount (only depends on height value)
  useEffect(() => {
    callbackRef.current?.(FIXED_HEIGHT);
  }, [FIXED_HEIGHT]);

  return {
    height: FIXED_HEIGHT,
    contentHeight: FIXED_HEIGHT,
    isNearMaxHeight: true, // Always at "max" since fixed
    handleContentSizeChange: () => {}, // No-op: height never changes
    handleFocus: () => {}, // No-op: no focus-based expansion
    handleBlur: () => {}, // No-op: no blur-based contraction
    MIN_HEIGHT: FIXED_HEIGHT,
    MAX_HEIGHT: FIXED_HEIGHT,
  };
}
